import type { ComponentProps, ReactElement } from "react";
import { cloneElement, useId, useRef } from "react";
import { toast } from "sonner";
import { z } from "zod";

import type { Handle } from "./creation-form";
import { CreationForm } from "./creation-form";

import { useDi } from "~/entities/di";
import { OrderQuery, OrderServicePorts } from "~/entities/order";
import { NotFoundError } from "~/shared/error";
import { Tk, TranslationParams, useTranslation2 } from "~/shared/i18n";
import { iife } from "~/shared/no-topic";
import { cn } from "~/shared/ui";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "~/shared/ui/alert-dialog";
import { Button } from "~/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/shared/ui/dialog";

const zMode = z.enum(["CREATE", "EDIT"]);
const Mode = zMode.Enum;
type Mode = z.infer<typeof zMode>;

type Props = ComponentProps<typeof Dialog> &
  Pick<ComponentProps<typeof CreationForm>, "services" | "selectedServices"> & {
    titleT?: TranslationParams.Type | undefined;
    onSuccessT?: TranslationParams.Type | undefined;
    onErrorT?: TranslationParams.Type | undefined;
    submitButtonT?: TranslationParams.Type | undefined;
    mode?: Mode | undefined;
    onSuccess?:
      | ((
          order: Parameters<
            NonNullable<
              NonNullable<
                Parameters<ReturnType<typeof OrderQuery.useCreate>["mutate"]>[1]
              >["onSuccess"]
            >
          >[0],
        ) => void)
      | undefined;
    showAlertDialogBeforeReset?: boolean | undefined;
    triggerProps?:
      | (Required<Pick<ComponentProps<typeof DialogTrigger>, "children">> &
          Omit<ComponentProps<typeof DialogTrigger>, "children">)
      | undefined;
    contentProps?: Omit<ComponentProps<typeof DialogContent>, "children"> | undefined;
  } & Pick<
    ComponentProps<typeof CreationForm>,
    | "defaultValues"
    | "moveIsScheduledForDisabled"
    | "showServicesControl"
    | "checkMoveIsScheduledFor"
  >;

function CreationDialog({
  services,
  selectedServices,
  titleT,
  onSuccessT,
  onErrorT,
  submitButtonT,
  defaultValues,
  moveIsScheduledForDisabled,
  showServicesControl,
  checkMoveIsScheduledFor,
  mode = Mode.CREATE,
  onSuccess,
  showAlertDialogBeforeReset,
  triggerProps,
  contentProps: { className: contentClassName, ...contentProps } = {},
  ...props
}: Props) {
  const {
    postproc: { tc },
  } = useTranslation2();
  const { orderService } = useDi();

  const { mutate: createOrder, isPending: isCreatingOrder } = OrderQuery.useCreate(orderService);
  const { mutate: updateOrder, isPending: isUpdatingOrder } = OrderQuery.useUpdate(orderService);
  const isPerforming = isCreatingOrder || isUpdatingOrder;

  const formId = useId();

  const handleFormSubmit: NonNullable<
    ComponentProps<typeof CreationForm>["formProps"]
  >["onSubmit"] = (data) => {
    if (mode === Mode.CREATE) {
      const { success, data: data2 } = OrderServicePorts.ProviderIos.Create.zIn.safeParse(data);

      if (!success)
        return console.error(
          "The data is invalid but should be valid. Check the validation schemes used for this function.",
        );

      createOrder(data2, {
        onSuccess: (order) => {
          toast.success(
            onSuccessT
              ? tc(...TranslationParams.toTuple(onSuccessT))
              : tc(Tk.ORDER_REQUEST_CREATED),
          );
          onSuccess?.(order);
        },
        onError: () => {
          toast.error(
            onErrorT
              ? tc(...TranslationParams.toTuple(onErrorT))
              : tc(Tk.FAILED_TO_CREATE_ORDER_REQUEST),
          );
        },
      });
    } else {
      const { success, data: data2 } = OrderServicePorts.ProviderIos.Update.zIn.safeParse(data);

      if (!success)
        return console.error(
          "The data is invalid but should be valid. Check the validation schemes used for this function.",
        );

      updateOrder(data2, {
        onSuccess: (order) => {
          toast.success(tc(Tk.CHANGES_SAVED_SUCCESSFULLY));
          onSuccess?.(order);
        },
        onError: (error) => {
          toast.error(tc(Tk.FAILED_TO_SAVE_CHANGES), {
            description: iife(() => {
              if (error instanceof NotFoundError) return tc(Tk.ORDER_NOT_FOUND);

              if (error instanceof OrderServicePorts.ProviderIos.Common.UpdateError) {
                if (
                  error.reason ===
                  OrderServicePorts.ProviderIos.Common.UpdateErrorReason.ORDER_COMPLETED
                )
                  return tc(Tk.THE_ORDER_CANNOT_BE_UPDATED_BECAUSE_IT_HAS_ALREADY_BEEN_FULFILLED);
              }

              return tc(Tk.AN_UNEXPECTED_ERROR_OCCURRED_PLEASE_TRY_AGAIN_LATER);
            }),
          });
        },
      });
    }
  };

  const formRef = useRef<Handle>(null);

  const { asChild: triggerAsChild = true, ...triggerProps_ } = triggerProps ?? {};

  const resetButton: ReactElement<ComponentProps<typeof Button>> = (
    <Button variant="outline" disabled={isPerforming}>
      {tc(Tk.RESET)}
    </Button>
  );
  const handleReset = () => {
    formRef.current?.hookForm.reset();
  };

  return (
    <Dialog {...props}>
      {triggerProps_ && <DialogTrigger asChild={triggerAsChild} {...triggerProps_} />}
      <DialogContent
        className={cn("flex max-h-4/5 flex-col md:max-w-2xl lg:max-w-4xl", contentClassName)}
        aria-describedby={undefined}
        {...contentProps}
      >
        <DialogHeader>
          <DialogTitle>
            {titleT ? tc(...TranslationParams.toTuple(titleT)) : tc(Tk.ORDER_A_MOVE)}
          </DialogTitle>
        </DialogHeader>
        <CreationForm
          ref={formRef}
          services={services}
          selectedServices={selectedServices}
          className="overflow-auto"
          defaultValues={defaultValues}
          moveIsScheduledForDisabled={moveIsScheduledForDisabled}
          showServicesControl={showServicesControl}
          checkMoveIsScheduledFor={checkMoveIsScheduledFor}
          formProps={{
            id: formId,
            onSubmit: handleFormSubmit,
          }}
        />
        <DialogFooter>
          {showAlertDialogBeforeReset ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>{resetButton}</AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {tc(Tk.ARE_YOU_SURE_YOU_WANT_TO_CLEAR_FORM_DATA)}
                  </AlertDialogTitle>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{tc(Tk.NO)}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleReset}>{tc(Tk.YES)}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            cloneElement(resetButton, {
              onClick: handleReset,
            })
          )}
          <Button disabled={isPerforming} form={formId}>
            {submitButtonT
              ? tc(...TranslationParams.toTuple(submitButtonT))
              : tc(Tk.SEND_AN_APPLICATION)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { CreationDialog };
